// Message format types
export type MessageFormat = 'JSON' | 'SIA_DC09' | 'PLAIN_TEXT' | 'CUSTOM_TEMPLATE';

// JSON Configuration interfaces
export interface JsonFieldsConfig {
    enabled: boolean;
    fields: string[];
}

export interface JsonConfig {
    includeAlertId: boolean;
    includeTimestamp: boolean;
    includeAlertType: boolean;
    includeUser: JsonFieldsConfig;
    includeLocation: JsonFieldsConfig;
    includeDevice: JsonFieldsConfig;
    includeMedia: JsonFieldsConfig;
}

// SIA DC-09 Configuration
export interface SiaDc09Config {
    accountPrefix: string;
    receiverNumber: string;
    // Options for data block content (backend generates the formatted message)
    includeGps: boolean;
    includeGpsTime: boolean;
    includeLivestreamUrl: boolean;
    includeUserName: boolean;
    includeUserId: boolean;
    includePhone: boolean;
    includeBatteryLevel: boolean;
    includeDeviceInfo: boolean;
}

// Alert type to SIA code mapping
export interface AlarmCodeMapping {
    EMERGENCY?: string;      // Default: NHA (Hold-up Alarm)
    MEDICAL?: string;        // Default: NMA (Medical Alarm)
    PTI_MANDOWN?: string;    // Default: NTA (Tilt Alarm / Man Down)
    PANIC?: string;          // Default: NPA (Panic Alarm)
    NO_ACTIVITY?: string;    // Default: NNA (No Activity Alarm)
    LOW_BATTERY?: string;    // Default: NLB (Low Battery)
    ZONE_ENTRY?: string;     // Default: NZA (Zone Alarm)
    ZONE_EXIT?: string;      // Default: NZR (Zone Restore)
    END?: string;            // Default: NRP (Repositioning / End alert)
}

// Default alarm code mapping
export const DEFAULT_ALARM_CODE_MAPPING: AlarmCodeMapping = {
    EMERGENCY: 'NHA',
    MEDICAL: 'NMA',
    PTI_MANDOWN: 'NTA',
    PANIC: 'NPA',
    NO_ACTIVITY: 'NNA',
    LOW_BATTERY: 'NLB',
    ZONE_ENTRY: 'NZA',
    ZONE_EXIT: 'NZR',
    END: 'NRP',
};

// Plain text configuration
export interface PlainTextConfig {
    template: string;
}

// Custom template configuration
export interface CustomTemplateConfig {
    template: string;
    outputType: 'text' | 'json' | 'xml';
}

// Main télésurveilleur configuration interface
export interface TeleSurveilleurConfig {
    messageFormat: MessageFormat;
    jsonConfig?: JsonConfig;
    siaDc09Config?: SiaDc09Config;
    plainTextConfig?: PlainTextConfig;
    customTemplateConfig?: CustomTemplateConfig;
    // Mapping des codes d'alarme par type d'alerte (utilisé par alert.service)
    alarmCodeMapping?: AlarmCodeMapping;
}

// Available template variables (for JSON, Plain Text, Custom formats)
export const TEMPLATE_VARIABLES = [
    { variable: '${alertId}', description: "Identifiant unique de l'alerte" },
    { variable: '${timestamp}', description: "Date/heure de l'alerte" },
    { variable: '${alertType}', description: 'Type (EMERGENCY, MEDICAL, PTI_MANDOWN)' },
    { variable: '${alertState}', description: 'État (START, END)' },
    { variable: '${alarmCode}', description: 'Code SIA DC-09 (NHA, NMA, NTA...)' },
    { variable: '${user.internalId}', description: 'ID interne Safee' },
    { variable: '${user.externalId}', description: 'ID externe (compte PTI)' },
    { variable: '${user.fullname}', description: 'Nom complet' },
    { variable: '${user.phone}', description: 'Téléphone' },
    { variable: '${user.email}', description: 'Email' },
    { variable: '${location.latitude}', description: 'Latitude GPS' },
    { variable: '${location.longitude}', description: 'Longitude GPS' },
    { variable: '${location.accuracy}', description: 'Précision GPS (mètres)' },
    { variable: '${device.model}', description: 'Modèle appareil' },
    { variable: '${device.os}', description: "Système d'exploitation" },
    { variable: '${device.batteryLevel}', description: 'Niveau batterie (%)' },
    { variable: '${media.livestreamUrl}', description: 'URL stream vidéo' },
    { variable: '${media.recordedAudioUrl}', description: 'URL enregistrement audio' },
];

// SIA DC-09 Alarm Codes (for reference and dropdown options)
export const SIA_DC09_ALARM_CODES = [
    { code: 'NHA', description: 'Hold-up Alarm - Urgence générale / Agression' },
    { code: 'NMA', description: 'Medical Alarm - Urgence médicale' },
    { code: 'NTA', description: 'Tilt Alarm - Chute détectée (Man Down)' },
    { code: 'NPA', description: 'Panic Alarm - Bouton SOS' },
    { code: 'NNA', description: 'No Activity Alarm - Absence de mouvement' },
    { code: 'NLB', description: 'Low Battery - Batterie faible' },
    { code: 'NZA', description: 'Zone Alarm - Entrée zone dangereuse' },
    { code: 'NZR', description: 'Zone Restore - Sortie zone sécurisée' },
    { code: 'NRP', description: 'Repositioning - Fin d\'alerte' },
];

// Alert types with descriptions (for UI mapping configuration)
export const ALERT_TYPES = [
    { key: 'EMERGENCY', label: 'Urgence', description: 'Alerte d\'urgence générale', defaultCode: 'NHA' },
    { key: 'MEDICAL', label: 'Médical', description: 'Urgence médicale', defaultCode: 'NMA' },
    { key: 'PTI_MANDOWN', label: 'Chute (PTI)', description: 'Détection de chute / Man Down', defaultCode: 'NTA' },
    { key: 'PANIC', label: 'Panique', description: 'Bouton SOS / Panique', defaultCode: 'NPA' },
    { key: 'NO_ACTIVITY', label: 'Inactivité', description: 'Absence de mouvement détectée', defaultCode: 'NNA' },
    { key: 'LOW_BATTERY', label: 'Batterie faible', description: 'Niveau batterie critique', defaultCode: 'NLB' },
    { key: 'ZONE_ENTRY', label: 'Entrée zone', description: 'Entrée dans une zone dangereuse', defaultCode: 'NZA' },
    { key: 'ZONE_EXIT', label: 'Sortie zone', description: 'Sortie d\'une zone sécurisée', defaultCode: 'NZR' },
    { key: 'END', label: 'Fin d\'alerte', description: 'Clôture / Repositionnement', defaultCode: 'NRP' },
] as const;

// Field options for JSON config
export const USER_FIELDS = ['internalId', 'externalId', 'fullname', 'phone', 'email'];
export const LOCATION_FIELDS = ['latitude', 'longitude', 'timestamp', 'accuracy'];
export const DEVICE_FIELDS = ['id', 'model', 'os', 'batteryLevel'];
export const MEDIA_FIELDS = ['livestreamUrl', 'recordedAudioUrl'];

// Default configurations
export const DEFAULT_JSON_CONFIG: JsonConfig = {
    includeAlertId: true,
    includeTimestamp: true,
    includeAlertType: true,
    includeUser: { enabled: true, fields: ['externalId', 'fullname', 'phone'] },
    includeLocation: { enabled: true, fields: ['latitude', 'longitude', 'accuracy'] },
    includeDevice: { enabled: false, fields: [] },
    includeMedia: { enabled: false, fields: [] },
};

export const DEFAULT_SIA_DC09_CONFIG: SiaDc09Config = {
    accountPrefix: 'L',
    receiverNumber: '0',
    includeGps: true,
    includeGpsTime: true,
    includeLivestreamUrl: false,
    includeUserName: true,
    includeUserId: false,
    includePhone: false,
    includeBatteryLevel: true,
    includeDeviceInfo: false,
};

export const DEFAULT_TELESURVEILLEUR_CONFIG: TeleSurveilleurConfig = {
    messageFormat: 'JSON',
    jsonConfig: DEFAULT_JSON_CONFIG,
    alarmCodeMapping: DEFAULT_ALARM_CODE_MAPPING,
};
